import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { X, Coins, Crown, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

export default function UnlockModal({ isOpen, onClose, matchId, coinCost = 1, onUnlocked }) {
  const router = useRouter();
  const { user, setAuth, revalidate } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [freshCoinBalance, setFreshCoinBalance] = useState(null);
  const [coinBreakdown, setCoinBreakdown] = useState(null);
  
  // Track the previous matchId to reset state when a new match is selected
  const prevMatchIdRef = useRef(matchId);
  
  // Reset success/error states when modal opens with a new matchId
  useEffect(() => {
    if (matchId !== prevMatchIdRef.current) {
      setSuccess(false);
      setError('');
      setLoading(false);
      prevMatchIdRef.current = matchId;
    }
  }, [matchId]);
  
  // Fetch fresh coin balance when modal opens
  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setError('');
      setLoading(false);
      
      // Fetch fresh coin balance from API
      fetch('/api/skillcoins/balance', { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && typeof data.balance === 'number') {
            setFreshCoinBalance(data.balance);
            setCoinBreakdown(data);
            // Also update auth context so it stays in sync
            if (setAuth && user) {
              setAuth({ ...user, skillcoins: data.balance });
            }
          }
        })
        .catch(() => {});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Use fresh balance if available, otherwise fall back to auth context
  const userCoins = freshCoinBalance !== null ? freshCoinBalance : (user?.skillcoins || 0);
  const hasEnoughCoins = userCoins >= coinCost;

  const handleUnlockWithCoin = async () => {
    if (!hasEnoughCoins) {
      router.push('/subscribe?type=coinpack&pack=starter');
      onClose();
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/skillcoins/spend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          match_id: matchId,
          coins: coinCost 
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to unlock');
      }

      const data = await response.json();
      setSuccess(true);
      
      // CRITICAL: Update user's coin balance IMMEDIATELY using API response
      if (typeof data.new_balance === 'number') {
        setFreshCoinBalance(data.new_balance);
      }
      
      // CRITICAL: Call onUnlocked IMMEDIATELY to set unlock state
      if (onUnlocked) onUnlocked(matchId);
      
      // Invalidate the NavBar's profile query so it fetches fresh coin balance
      // Use exact: false to match all queries starting with ['user', 'profile']
      queryClient.invalidateQueries({ 
        queryKey: ['user', 'profile'],
        exact: false
      });
      
      // Also revalidate auth context
      if (revalidate) {
        revalidate({ silent: true });
      }

      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (err) {
      console.error('Unlock error:', err);
      setError(err.message || 'Failed to unlock content');
    } finally {
      setLoading(false);
    }
  };

  const handleGoPremium = () => {
    router.push('/subscribe?tier=premium');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-700 rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition"
        >
          <X className="w-5 h-5" />
        </button>

        {success ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Unlocked!</h3>
            <p className="text-gray-400">Content is now available</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-2">Unlock Content</h3>
              <p className="text-gray-400 text-sm">Choose how to unlock this prediction</p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-center">
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleUnlockWithCoin}
                disabled={loading}
                className="w-full p-4 rounded-xl border-2 border-[#DAA520] bg-[#DAA520]/10 hover:bg-[#DAA520]/20 transition-all flex items-center gap-4 disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#B8860B] to-[#DAA520] flex items-center justify-center">
                  <Coins className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-bold text-white">
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Unlocking...
                      </span>
                    ) : (
                      `Unlock with ${coinCost} SkillCoin${coinCost > 1 ? 's' : ''}`
                    )}
                  </div>
                  <div className="text-sm text-gray-400">
                    {hasEnoughCoins ? (
                      <span>You have <span className="text-[#DAA520] font-semibold">{userCoins}</span> coins</span>
                    ) : (
                      <span className="text-orange-400">Not enough coins - Buy more</span>
                    )}
                  </div>
                  {coinBreakdown && (
                    <div className="text-xs text-gray-500 mt-1">
                      {coinBreakdown.free_balance} free
                      {coinBreakdown.free_expires_at
                        ? ` (expires ${new Date(coinBreakdown.free_expires_at).toLocaleDateString()})`
                        : ''}
                      {' · '}{coinBreakdown.non_expiring_balance} non-expiring
                    </div>
                  )}
                </div>
              </button>

              <button
                onClick={handleGoPremium}
                disabled={loading}
                className="w-full p-4 rounded-xl border-2 border-[#3EA8F5] bg-[#3EA8F5]/10 hover:bg-[#3EA8F5]/20 transition-all flex items-center gap-4 disabled:opacity-50"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3EA8F5] to-blue-600 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-bold text-white">Go Premium</div>
                  <div className="text-sm text-gray-400">Unlimited access from R199</div>
                </div>
              </button>
            </div>

            <p className="text-center text-xs text-slate-500 mt-4">
              Premium subscribers get unlimited access to all predictions
            </p>
          </>
        )}
      </div>
    </div>
  );
}
